import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MomLayout from './MomLayout';
import { Button } from '../../components/ui/button';
import { momApi } from '../../lib/mom-api';
import { useMomAuth } from '../../context/MomAuthContext';
import { toast } from 'sonner';
import { ArrowRight, ArrowLeft, Loader2, Baby, Calendar, Heart } from 'lucide-react';

const STEPS = ['week', 'gender', 'due_date'];

export default function MomOnboardingPage() {
  const { setMom } = useMomAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  const [week, setWeek] = useState(12);
  const [gender, setGender] = useState(null); // 'female' | 'male' | 'unknown'
  const [dueDate, setDueDate] = useState('');

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const finish = async () => {
    setBusy(true);
    try {
      const updated = await momApi.saveOnboarding({
        pregnancy_week: week,
        baby_gender: gender || 'unknown',
        due_date: dueDate || null,
      });
      setMom(updated);
      toast.success('Harika! Bebek yolculuğun başlasın.');
      navigate('/mom/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.message || 'Kaydetme başarısız');
    } finally {
      setBusy(false);
    }
  };

  return (
    <MomLayout>
      {/* Progress dots */}
      <div className="flex items-center gap-2 mb-10" data-testid="mom-onboarding-progress">
        {STEPS.map((_, i) => (
          <div
            key={i}
            className={`h-1.5 rounded-full transition-all ${i <= step ? 'bg-[#FB7185] flex-1' : 'bg-[#FFE4DF] w-6'}`}
          />
        ))}
      </div>

      {/* Step 0: pregnancy week */}
      {step === 0 && (
        <div data-testid="mom-onboarding-step-week">
          <div className="w-16 h-16 rounded-3xl bg-[#FFE4DF] flex items-center justify-center mb-5">
            <Baby className="w-8 h-8 text-[#FB7185]" />
          </div>
          <h1 className="text-3xl font-bold text-[#2D2422] mb-2 leading-tight">
            Kaçıncı haftadasın?
          </h1>
          <p className="text-base text-[#7A6C68] mb-8">Bebeğin gelişimini haftaya göre takip edeceğiz.</p>

          <div className="bg-white rounded-3xl p-8 shadow-sm border border-[#F3E5E0]">
            <div className="text-center mb-6">
              <p className="text-6xl font-bold text-[#FB7185]" data-testid="mom-onboarding-week-display">{week}</p>
              <p className="text-sm text-[#7A6C68] mt-1">. hafta</p>
            </div>
            <input
              type="range"
              min="1"
              max="40"
              value={week}
              onChange={(e) => setWeek(parseInt(e.target.value))}
              className="w-full accent-[#FB7185] h-2"
              data-testid="mom-onboarding-week-slider"
            />
            <div className="flex justify-between text-xs text-[#7A6C68] mt-2">
              <span>1. hafta</span>
              <span>40. hafta</span>
            </div>
          </div>
        </div>
      )}

      {/* Step 1: gender */}
      {step === 1 && (
        <div data-testid="mom-onboarding-step-gender">
          <div className="w-16 h-16 rounded-3xl bg-[#FEF3C7] flex items-center justify-center mb-5">
            <Heart className="w-8 h-8 text-[#FBBF24]" />
          </div>
          <h1 className="text-3xl font-bold text-[#2D2422] mb-2 leading-tight">
            Bebeğinin cinsiyeti?
          </h1>
          <p className="text-base text-[#7A6C68] mb-8">Bilmiyorsan sorun değil — sonra güncelleyebilirsin.</p>

          <div className="grid grid-cols-3 gap-3">
            <GenderCard active={gender === 'female'} label="Kız" emoji="👧🏻" onClick={() => setGender('female')} testid="mom-onboarding-gender-female" color="#FB7185" />
            <GenderCard active={gender === 'male'} label="Erkek" emoji="👦🏻" onClick={() => setGender('male')} testid="mom-onboarding-gender-male" color="#60A5FA" />
            <GenderCard active={gender === 'unknown'} label="Sürpriz" emoji="✨" onClick={() => setGender('unknown')} testid="mom-onboarding-gender-unknown" color="#A78BFA" />
          </div>
        </div>
      )}

      {/* Step 2: due date */}
      {step === 2 && (
        <div data-testid="mom-onboarding-step-due-date">
          <div className="w-16 h-16 rounded-3xl bg-[#DBEAFE] flex items-center justify-center mb-5">
            <Calendar className="w-8 h-8 text-[#3B82F6]" />
          </div>
          <h1 className="text-3xl font-bold text-[#2D2422] mb-2 leading-tight">
            Tahmini doğum tarihi?
          </h1>
          <p className="text-base text-[#7A6C68] mb-8">İsteğe bağlı — doktorundan aldığın tarihi girebilirsin.</p>

          <div className="bg-white rounded-3xl p-6 shadow-sm border border-[#F3E5E0]">
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full h-14 px-4 rounded-2xl border border-[#F3E5E0] bg-[#FFFAF8] text-base text-[#2D2422] focus:border-[#FB7185] outline-none"
              data-testid="mom-onboarding-due-date-input"
            />
            <button
              type="button"
              onClick={() => setDueDate('')}
              className="mt-3 text-sm text-[#7A6C68] hover:text-[#FB7185] w-full text-center"
              data-testid="mom-onboarding-due-date-skip-btn"
            >
              Şimdilik geç
            </button>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center gap-3 mt-10">
        {step > 0 && (
          <Button variant="outline" onClick={back} className="h-14 rounded-2xl border-[#F3E5E0] px-5"
            data-testid="mom-onboarding-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        )}
        {step < STEPS.length - 1 ? (
          <Button onClick={next} className="flex-1 h-14 rounded-2xl bg-[#FB7185] hover:bg-[#F43F5E] text-white text-base font-semibold"
            data-testid="mom-onboarding-next-btn">
            Devam<ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        ) : (
          <Button onClick={finish} disabled={busy} className="flex-1 h-14 rounded-2xl bg-[#FB7185] hover:bg-[#F43F5E] text-white text-base font-semibold"
            data-testid="mom-onboarding-finish-btn">
            {busy ? (<><Loader2 className="w-5 h-5 mr-2 animate-spin" />Kaydediliyor...</>) : (<>Başla<ArrowRight className="w-5 h-5 ml-2" /></>)}
          </Button>
        )}
      </div>
    </MomLayout>
  );
}

const GenderCard = ({ active, label, emoji, onClick, testid, color }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-3xl p-5 transition-all text-center ${
      active ? 'ring-2 shadow-md' : 'border border-[#F3E5E0]'
    }`}
    style={active ? { backgroundColor: '#FFFFFF', borderColor: color, boxShadow: `0 0 0 2px ${color}` } : { backgroundColor: '#FFFFFF' }}
    data-testid={testid}
  >
    <div className="text-3xl mb-2">{emoji}</div>
    <p className="text-sm font-medium text-[#2D2422]">{label}</p>
  </button>
);
